import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import BaseModal from "../BaseModal/BaseModal";
import style from "./BoardSettingsModal.module.css";
import { getBoardPasswords, updateBoardPasswords } from "../../api/boards";
import { getBoardMembers, assignBoardMember, removeBoardMember } from "../../api/members";
import { useMemberSearch } from "../../hooks/useMemberSearch";

// ── Autocomplete з чіпами для вибору членів на борді ──────────────────────────
const MemberPicker = ({ role, currentMembers, onAssign, onRemove }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const addedIds = new Set(currentMembers.map(m => m.id));
  const suggestions = useMemberSearch(query, addedIds);

  const handleSelect = async (member) => {
    await onAssign(member, role);
    setQuery("");
  };

  const roleMembers = currentMembers.filter(m => m.role === role);

  return (
    <div className={style.pickerContainer}>
      <div className={style.chipsRow}>
        {roleMembers.map(m => (
          <span key={m.id} className={style.chip}>
            {m.full_name}
            <button className={style.chipRemove} onClick={() => onRemove(m)}>×</button>
          </span>
        ))}
        <div className={style.autocompleteWrap}>
          <input
            className={style.pickerInput}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t('board_settings.search')}
            autoComplete="off"
          />
          {suggestions.length > 0 && (
            <div className={style.suggestions}>
              {suggestions.map(m => (
                <button key={m.id} className={style.suggestionItem} onMouseDown={() => handleSelect(m)}>
                  <span>{m.full_name}</span>
                  <span className={style.suggestionEmail}>{m.email}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Головний компонент ─────────────────────────────────────────────────────────
const BoardSettingsModal = ({ board, onClose }) => {
  const { t } = useTranslation();
  const [boardMembers, setBoardMembers] = useState([]);
  const [managerPassword, setManagerPassword] = useState("");
  const [viewerPassword, setViewerPassword] = useState("");
  const [initialManagerPassword, setInitialManagerPassword] = useState("");
  const [initialViewerPassword, setInitialViewerPassword] = useState("");
  const [statusCommentsEnabled, setStatusCommentsEnabled] = useState(false);
  const [initialStatusCommentsEnabled, setInitialStatusCommentsEnabled] = useState(false);
  const [settingsChanged, setSettingsChanged] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [membersData, passwordsData] = await Promise.all([
          getBoardMembers(board.company_slug, board.slug),
          getBoardPasswords(board.id),
        ]);
        setBoardMembers(membersData.members || []);
        setManagerPassword(passwordsData.manager_password || "");
        setViewerPassword(passwordsData.viewer_password || "");
        setInitialManagerPassword(passwordsData.manager_password || "");
        setInitialViewerPassword(passwordsData.viewer_password || "");
        setStatusCommentsEnabled(Boolean(passwordsData.status_comments_enabled));
        setInitialStatusCommentsEnabled(Boolean(passwordsData.status_comments_enabled));
      } catch (err) {
        setError(t('board_settings.load_error'));
      }
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [board.id, board.slug]);

  const handleAssign = async (member, role) => {
    try {
      await assignBoardMember(board.company_slug, board.slug, member.id, role);
      setBoardMembers(prev => {
        const without = prev.filter(m => m.id !== member.id);
        return [...without, { ...member, role }];
      });
    } catch (err) {
      setError(err.message || t('board_settings.assign_error'));
    }
  };

  const handleRemove = async (member) => {
    try {
      await removeBoardMember(board.company_slug, board.slug, member.id);
      setBoardMembers(prev => prev.filter(m => m.id !== member.id));
    } catch (err) {
      setError(err.message || t('board_settings.remove_error'));
    }
  };

  const handleSaveSettings = async () => {
    try {
      await updateBoardPasswords(board.id, managerPassword, viewerPassword, statusCommentsEnabled);
      setInitialManagerPassword(managerPassword);
      setInitialViewerPassword(viewerPassword);
      setInitialStatusCommentsEnabled(statusCommentsEnabled);
      setSettingsChanged(false);
    } catch (err) {
      setError(err.message || t('board_settings.save_error'));
    }
  };

  const handleResetSettings = () => {
    setManagerPassword(initialManagerPassword);
    setViewerPassword(initialViewerPassword);
    setStatusCommentsEnabled(initialStatusCommentsEnabled);
    setSettingsChanged(false);
  };

  return (
    <BaseModal open={true} onClose={onClose} scrollable>
      <p className={style.title}>{t('board_settings.title')}</p>
      <p className={style.subTitle}>{board.dashboard_name}</p>
      {error && (
        <p className={style.errorBanner} onClick={() => setError("")}>{error} ✕</p>
      )}

      <label className={style.statusCommentsToggle}>
        <input
          type="checkbox"
          checked={statusCommentsEnabled}
          onChange={(e) => {
            setStatusCommentsEnabled(e.target.checked);
            setSettingsChanged(true);
          }}
        />
        <span>{t('board_settings.enable_comment_history')}</span>
      </label>

      <div className={style.divider} />

      {/* ── Іменний доступ ── */}
      <div className={style.rolesGrid}>
        <div className={style.section}>
          <p className={style.inputLabel}>{t('board_settings.admins')}</p>
          <MemberPicker
            role="admin"
            currentMembers={boardMembers}
            onAssign={handleAssign}
            onRemove={handleRemove}
          />
        </div>

        <div className={style.section}>
          <p className={style.inputLabel}>{t('board_settings.managers')}</p>
          <MemberPicker
            role="manager"
            currentMembers={boardMembers}
            onAssign={handleAssign}
            onRemove={handleRemove}
          />
        </div>

        <div className={style.section}>
          <p className={style.inputLabel}>{t('board_settings.viewers')}</p>
          <MemberPicker
            role="viewer"
            currentMembers={boardMembers}
            onAssign={handleAssign}
            onRemove={handleRemove}
          />
        </div>
      </div>

      {/* ── Доступ за паролем ── */}
      <div className={style.divider} />

      <p className={style.sectionLabel}>{t('board_settings.password_access')}</p>
      <p className={`grey-text ${style.passwordHint}`}>
        {t('board_settings.password_hint')}
      </p>

      <div className={style.passwordsGrid}>
        <div className={style.passwordField}>
          <label className={style.inputLabel}>{t('board_settings.manager_password')}</label>
          <div className={style.passwordInputWrap}>
            <input
              type={showPasswords ? "text" : "password"}
              className={style.passwordInput}
              value={managerPassword}
              autoComplete="new-password"
              onChange={(e) => { setManagerPassword(e.target.value); setSettingsChanged(true); }}
            />
            <button className={style.eyeBtn} onClick={() => setShowPasswords(p => !p)} type="button">
              {showPasswords ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
            </button>
          </div>
        </div>

        <div className={style.passwordField}>
          <label className={style.inputLabel}>{t('board_settings.viewer_password')}</label>
          <div className={style.passwordInputWrap}>
            <input
              type={showPasswords ? "text" : "password"}
              className={style.passwordInput}
              value={viewerPassword}
              autoComplete="new-password"
              onChange={(e) => { setViewerPassword(e.target.value); setSettingsChanged(true); }}
            />
            <button className={style.eyeBtn} onClick={() => setShowPasswords(p => !p)} type="button">
              {showPasswords ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
            </button>
          </div>
        </div>
      </div>

      <div className={style.buttonsContainer}>
        <button
          className={`button thirdly-button ${style.cancelButton}`}
          onClick={handleResetSettings}
          disabled={!settingsChanged}
        >
          {t('common.cancel')}
        </button>
        <button
          className={`button ${style.saveButton}`}
          onClick={handleSaveSettings}
          disabled={!settingsChanged}
        >
          {t('common.save')}
        </button>
      </div>
    </BaseModal>
  );
};

export default BoardSettingsModal;
